import {useState, useRef} from 'react';
import PropTypes from 'prop-types';
import Util from '../util/Util';
import FileUtils from '../util/FileUtils';
import Locale from '../util/Locale';
import './GridPanel.scss';

function GridPanel({
    onAction = null,
    onClick = null,
    onToggleSelected = null,
    isAdvertising = false,
    data = null,
    index = '0',
    selected = undefined, // So tag view won't show grid checkboxes
    onDragStart = null,
    advertisingError = null
}) {
    const [advError, setAdvError] = useState(advertisingError);
    const [hasCopied, setHasCopied] = useState(false);
    const doubleClickTimerId = useRef(null);

    const handleDoubleClicks = event => {
        if (onAction) {
            if (doubleClickTimerId.current !== null) {
                clearTimeout(doubleClickTimerId.current);
                doubleClickTimerId.current = null;
            }
            if (event.detail === 1) {
                if (event.persist) {
                    event.persist();
                }
                doubleClickTimerId.current = setTimeout(() => onAction(event), 350);
            } else if (event.detail === 2) {
                onAction(event);
            }
        }
    };

    const getFileDetails = file => {
        if (file.width && file.height) {
            return (
                <div>
                    <span className="file-details">
                        {file.width} x {file.height}
                    </span>
                </div>
            );
        }
        return null;
    };

    const toggleSelected = (event, idx) => {
        event.stopPropagation();
        if (onToggleSelected) {
            onToggleSelected();
        } else {
            // Assuming `selected` is a state variable, you might need to manage it here
        }
        if (onClick) {
            onClick(idx);
        }
    };

    const videoError = () => {
        setAdvError(true);
    };

    const handleCopyLink = event => {
        event.stopPropagation();
        Util.copyToClipboard(data, () => {
            setHasCopied(true);
            setTimeout(() => {
                setHasCopied(false);
            }, 1000);
        });
    };

    const renderContents = (fileType, isDraggable) => {
        if (advError) {
            return <img alt="Thumbnail" className="video-error" src={Util.getPlaceholderByType(fileType)} />;
        }

        return isAdvertising
            ? Util.getVideoThumbnail(data, onAction, isDraggable, videoError)
            : Util.getThumbnail(data, onAction, isDraggable);
    };

    const renderAspectRatio = aspectRatio => {
        return (
            <div className="aspect-ratio">
                <span>{aspectRatio}</span>
            </div>
        );
    };

    if (data) {
        const fileType = Util.getFileTypeLabelFromMimeType(data.mimeType, data, true);
        const fileDetails = getFileDetails(data);
        const isDraggable = selected;
        const aspectRatio = FileUtils.getImageAspectRatio(data.width, data.height);
        return (
            <div
                className={selected ? 'grid-panel selected' : 'grid-panel'}
                onClick={Util.isWidget() ? handleDoubleClicks : onAction}
                onDragStart={onDragStart}
                draggable={isDraggable}
                role="presentation"
                onKeyDown={() => null}
            >
                <div
                    className={
                        fileType !== Util.FILE_TYPE.IMAGE
                            ? `panel-preview flex ${'status' in data && data.status && 'processing'}`
                            : 'panel-preview flex'
                    }
                    draggable={isDraggable}
                >
                    {fileType === Util.FILE_TYPE.IMAGE && aspectRatio && renderAspectRatio(aspectRatio)}
                    {selected !== undefined && (
                        <nsemble-checkbox
                            variant
                            onClick={e => {
                                toggleSelected(e, index);
                            }}
                            checked={selected ? true : null}
                            // needs to be "class" not "className" as this is an nSemble component, which expects the attribute name "class"
                            class="white-border"
                        />
                    )}
                    {renderContents(data, fileType, isDraggable, onAction)}
                </div>
                <div className="panel-details">
                    <span className="file-name">{Util.decodeLabel(data.label)}</span>
                    <span className="file-info">
                        <button
                            type="button"
                            className="copy-link-icon"
                            onClick={handleCopyLink}
                            title={Locale.getText('COPY_LINK_ASSET')}
                            aria-label={Locale.getText('COPY_LINK_ASSET')}
                        >
                            {hasCopied ? (
                                <svg
                                    fill="none"
                                    height="16"
                                    stroke="#00528A"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    width="16"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            ) : (
                                Util.getClipboardCopyIcon('#00528A')
                            )}
                        </button>
                        <span className={fileDetails ? 'file-type-dot' : 'file-type'}>
                            {Locale.getText(fileType).toUpperCase()}
                        </span>
                        {getFileDetails(data)}
                    </span>
                </div>
                <div className="ml3-tooltip">
                    <div className="ml3-tooltip-text">{Util.decodeLabel(data.label)}</div>
                </div>
            </div>
        );
    }
    return null;
}

GridPanel.propTypes = {
    onAction: PropTypes.func,
    onClick: PropTypes.func,
    onToggleSelected: PropTypes.func,
    isAdvertising: PropTypes.bool,
    data: PropTypes.shape({
        fileSize: PropTypes.number,
        height: PropTypes.number,
        id: PropTypes.string,
        label: PropTypes.string,
        libraryId: PropTypes.string,
        mimeType: PropTypes.string,
        name: PropTypes.string,
        status: PropTypes.number,
        thumbName: PropTypes.string,
        width: PropTypes.number,
        altText: PropTypes.string
    }),
    onDragStart: PropTypes.func,
    index: PropTypes.string,
    selected: PropTypes.bool,
    advertisingError: PropTypes.bool
};

export default GridPanel;
